import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import LoadingScreen from '../loadingScreenComp/LoadingScreen';
import { InputGroupPropsTypes } from '../inputComp/InputGroupType';
import { CloseIcon, DownIcon, FormWorkflowIcon, SearchIcon, UpIcon, UserIcon } from '../iconSet/IconSet';
import InputGroup from '../inputComp/InputGroup';
import UserDataPicker, { UserColumnDataType, UserDataRowType } from '../dataPicker/UserDataPicker';
import { APICallGetAllRecipientRequest, ApiGetAllUsers, ApiGetAllWorkflows, ApiGetUsersByWorkflowId, APIRequestSaveRecipients, APIRequestSaveRecipientType, APIResponseGetRecipientRequestTypes } from './SetupRecipientService';
import WorkflowDataPicker, { WorkflowColumnDataType, WorkflowDataRowType } from '../dataPicker/WorkflowDataPicker';
import AlertDialog from '../popupComp/AlertDialog';


const workflowColumnData: WorkflowColumnDataType[] = [
    {value: "name", name: "Name"},
    {value: "department", name: "Department"},
    {value: "hazard_category", name: "Hazard Category"}
]

const userColumnData: UserColumnDataType[] = [
    {value: "name", name: "Name"},
    {value: "department", name: "Department"},
    {value: "role", name: "Role"}
]

const SetupRecipients = () => {
    const [alertDialogState, setAlertDialogState] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertDescription, setAlertDescription] = useState('');
    const [alertYesCallback, setAlertYesCallback] = useState<(() => void) | undefined>(undefined); // for Yes
    const [alertOkCallback, setAlertOkCallback] = useState<(() => void) | undefined>(undefined);  // for OK

    const showAlertDialog = (title: string, description: string, yesCallback?: () => void, okCallback?: () => void ) => {
        setAlertTitle(title);
        setAlertDescription(description);
        setAlertYesCallback(() => yesCallback); // Will show Yes/Cancel if defined
        setAlertOkCallback(() => okCallback);   // Will trigger only on OK
        setAlertDialogState(true);
    }

    // Loading screen
    const [isLoading, setIsLoading] = useState<boolean>(false);
    //const [filteredUsersData, setFilteredUserData] = useState<DataType[]>([]);
    //const allWorkflows = useRef<DataType[]>([]);

    // Data picker status
    const [isWorkflowDataPickerOpen, setIsWorkflowDataPikerOpen] = useState<boolean>(false);
    const [isUserDataPickerOpen, setIsUserDataPikerOpen] = useState<boolean>(false);

    const pickerUserRowDataRef = useRef<UserDataRowType[]>([])
    const [pickerUserRowData, setPickerUserRowData] = useState<UserDataRowType[]>([]);
    const [filteredUserRows, setFilteredUserRow] = useState<UserDataRowType[]>([]);

    const pickerWorkflowRowDataRef = useRef<WorkflowDataRowType[]>([])
    const [pickerWorkflowRowData, setPickerWorkflowRowData] = useState<WorkflowDataRowType[]>([]);

    const workflowRef = useRef<HTMLInputElement | null>(null)
    const workflowUserRef = useRef<HTMLInputElement | null>(null)

    const [selectUserRow, setSelectUserRow] = useState<UserDataRowType | null>(null)
    const [selectWorkflowRow, setSelectWorkflowRow] = useState<WorkflowDataRowType | null>(null)

    const [selectedRow, setSelectedRow] = useState<string[]>([])

    const formDataList: InputGroupPropsTypes[] = [
        {
            type: 'text',
            icon: <FormWorkflowIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Select workflow',
            label: 'Workflow',
            name: 'Workflow',
            required: true,
            componentInputElement: workflowRef,
            disabled: true,
            dataPicker: true,
			isDataPickerOpen: isWorkflowDataPickerOpen,
			setIsDataPickerOpen: setIsWorkflowDataPikerOpen,
        },
        {
            type: 'text',
            icon: <UserIcon width="1.5rem" color="var(--color-main-white)" />,
            color: 'var(--color-accent-bg)',
            placeholder: 'Select user',
            label: 'User',
            name: 'user',
            required: true,
            componentInputElement: workflowUserRef,
            disabled: true,
            dataPicker: selectWorkflowRow ? true : false,
			isDataPickerOpen: isUserDataPickerOpen,
			setIsDataPickerOpen: setIsUserDataPikerOpen,
        }
    ]

    const handleClickUserRow = (event: React.MouseEvent<HTMLDivElement>, item: UserDataRowType): void => {
        if (!workflowUserRef.current) return

        workflowUserRef.current.value = item.data.name
        workflowUserRef.current.name = item.id
        setSelectUserRow(item)
        setIsUserDataPikerOpen(false)
    }

    const handleClickWorkflowRow = (event: React.MouseEvent<HTMLDivElement>, item: WorkflowDataRowType): void => {
        if (!workflowRef.current) return

        workflowRef.current.value = item.data.name
        workflowRef.current.name = item.id
        setSelectWorkflowRow(item)
        setIsWorkflowDataPikerOpen(false)
    }

    // Get all users
    const getAllUsers = async () => {
        const response = await ApiGetAllUsers()
        const fixedData: UserDataRowType[] = []

        response.data.map(item => {
            fixedData.push({id: item.username, data: {name: item.firstname, department: item.department, role: item.role}})
        })
        setFilteredUserRow(fixedData)
    }

    // Get all workflows
    const getAllWorkflows = async () => {
        const response = await ApiGetAllWorkflows()
        const fixedData: WorkflowDataRowType[] = []

        response.data.map(item => {
            fixedData.push({id: item.warningId, data: {name: item.username, department: item.departments, hazardType: item.hazardType}})
        })
        setPickerWorkflowRowData(fixedData)
    }

    useEffect(() => {
        getAllWorkflows()
    }, [])

    const handleRecipientSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const text = event.target.value
        if (text === "")
        {
            setFilteredUserRow(pickerUserRowDataRef.current)
        }
        else 
        {
            setFilteredUserRow(prevState => {
                return pickerUserRowDataRef.current.filter(item => item.data.name.toLowerCase().includes(text.toLowerCase()))
            })   
        }
    }

    const handleClickCheckboxClick = (event: React.MouseEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const id = input.name;
      
        setSelectedRow(prevState => {
            if (prevState.includes(id)) 
            {
                
                return prevState.filter(item => item !== id);
            } 
            else 
            {
                return [...prevState, id];
            }
        });
    };

    const getUsersByWorkflowId = async (id: string) => {
        const response = await ApiGetUsersByWorkflowId(id)
        
        if (response.status)
        {
            const fixedData: UserDataRowType[] = []
            response.data.map(item => {
                fixedData.push({id: item.username, data: {name: item.firstName, department: item.departmentName, role: item.roleName}})
            })
            setPickerUserRowData(fixedData)
        }
        else
        {
            console.error("error while retrieving users")
        }
    }

    const getSelectedRecipients = async (flowId: string) => {
        if (!selectUserRow) return 
        const response = await APICallGetAllRecipientRequest(flowId, selectUserRow.id)
        const selectedIds = response.data.map((item: APIResponseGetRecipientRequestTypes) => item.recipient); 
        setSelectedRow(selectedIds);
    }

    useEffect(() => {
        if (selectWorkflowRow && !selectUserRow)
        {
            getUsersByWorkflowId(selectWorkflowRow.id)
        }
        else if (selectUserRow && selectWorkflowRow)
        {
            getAllUsers()
            getSelectedRecipients(selectWorkflowRow.id)
        }

    }, [selectUserRow, selectWorkflowRow])

    const handleClickSave = async () => {

        if (workflowRef.current && workflowUserRef.current && selectWorkflowRow && selectUserRow)
        {
            const body: APIRequestSaveRecipientType = {
                workflow: selectWorkflowRow.id,
                user: selectUserRow.id,
                recipients: selectedRow
            }
            const response = await APIRequestSaveRecipients(body)

            if (response)
            {
                showAlertDialog("Success", "✅ recipients saved successfully!", undefined, () => window.location.reload());
            }
            else
            {
                return showAlertDialog("Error", "❌ recipient saving failed");
            }
        }
    }

    // true = A -> Z
    // false = Z -> A
    const [firstNameSort, setFirstNameSort] = useState<Boolean>(true)
    const firstNameSortHandle = () => {
        setFirstNameSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredUserRow(prevState => {
            return [...prevState].sort((a, b) => {
                const firstNameComparison = firstNameSort ? a.data.name.localeCompare(b.data.name) : b.data.name.localeCompare(a.data.name)
                return firstNameComparison
            })
        })
    }, [firstNameSort])

    const [departmentSort, setDepartmentSort] = useState<Boolean>(true)
    const departmentSortHandle = () => {
        setDepartmentSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredUserRow(prevState => {
            return [...prevState].sort((a, b) => {
                const departmentComparison = departmentSort ? a.data.department.localeCompare(b.data.department) : b.data.department.localeCompare(a.data.department)
                return departmentComparison
            })
        })
    }, [departmentSort])

    const [roleSort, setRoleSort] = useState<Boolean>(true)
    const roleSortHandle = () => {
        setRoleSort(prevState => !prevState)
    }
    useEffect(() => {
        setFilteredUserRow(prevState => {
            return [...prevState].sort((a, b) => {
                const roleComparison = roleSort ? a.data.role.localeCompare(b.data.role) : b.data.role.localeCompare(a.data.role)
                return roleComparison
            })
        })
    }, [departmentSort])

    const handleSelectAllCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked

        if (isChecked) {
            // Select all user IDs
            const allIds = filteredUserRows.map((item) => item.id);
            setSelectedRow(allIds);
        } else {
            // Deselect all
            setSelectedRow([]);
        }
    }

    return (
        <div className={`container-wrapper`}>
            <span className={`title`}>Setup Recipients</span>
            <div className='setup-recipient'>
                {formDataList.map((field, index) => (
                    <InputGroup key={index} {...field}/>
                ))}
                {selectUserRow && selectWorkflowRow && (
                    <div className="recipients-table">
                        <span className="title">Select recipients</span>
                        <div className="search-bar-wrapper">
                            <i>
                                <SearchIcon width="1.2rem" color="gray" />
                            </i>
                            <input type="text" placeholder="Search recipients" onChange={(e) => handleRecipientSearchChange(e)} />
                        </div>
                        <div className="table-body" style={{overflow: 'hidden', overflowX: 'auto'}}>
                            <div className="column">
                                <span>
                                    <input type="checkbox" className='select-all-checkbox' onChange={(e) => handleSelectAllCheckbox(e)}/>
                                </span>
                                <span onClick={(e) => firstNameSortHandle()}>
                                    First name
                                    <div>
                                        {firstNameSort ? (
                                            <UpIcon width="1rem" color="var(--color-main-white)" />
                                        ) : (
                                            <DownIcon width="1rem" color="var(--color-main-white)" />
                                        )}
                                    </div>
                                </span>
                                <span onClick={(e) => departmentSortHandle()}>
                                    Department
                                    <div>
                                        {departmentSort ? (
                                            <UpIcon width="1rem" color="var(--color-main-white)" />
                                        ) : (
                                            <DownIcon width="1rem" color="var(--color-main-white)" />
                                        )}
                                    </div>
                                </span>
                                <span onClick={(e) => roleSortHandle()}>
                                    Role
                                    <div>
                                        {roleSort ? (
                                            <UpIcon width="1rem" color="var(--color-main-white)" />
                                        ) : (
                                            <DownIcon width="1rem" color="var(--color-main-white)" />
                                        )}
                                    </div>
                                </span>
                            </div>
                            {filteredUserRows.length > 0 ? (
                                <div className="table-row-body">
                                    {filteredUserRows.map((item, index) => (
                                        <div className="row" key={index}>
                                            <span style={{justifyContent: "center"}}>
                                                <input type="checkbox" name={item.id} id="" checked={selectedRow.includes(item.id)} onClick={(e) => handleClickCheckboxClick(e)} />
                                            </span>
                                            <span>{item.data.name}</span>
                                            <span>{item.data.department}</span>
                                            <span>{item.data.role}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : ((
                                <div className="no-data-wrapper">No any data available</div>
                            ))}
                            
                        </div>
                        <div className="button-wrapper" >
                            <button onClick={()=> window.location.reload()} style={{backgroundColor: "var(--color-main-red)"}}>Reset</button>
                            <button onClick={()=>
                                showAlertDialog(
                                    "Confirm Submit",
                                    "Are you sure you want to submit this recipients?",
                                    () => handleClickSave(), undefined)
                            }>Save</button>
                        </div>
                    </div>
                )}
            </div>
            {isUserDataPickerOpen && (
                <UserDataPicker setCloseState={setIsUserDataPikerOpen} columnData={userColumnData} rowData={pickerUserRowData} handleRowClick={handleClickUserRow}/>
            )}
            {isWorkflowDataPickerOpen && (
                <WorkflowDataPicker setCloseState={setIsWorkflowDataPikerOpen} columnData={workflowColumnData} rowData={pickerWorkflowRowData} handleRowClick={handleClickWorkflowRow}/>
            )}
            {alertDialogState && (
                <AlertDialog
                    title={alertTitle}
                    description={alertDescription}
                    windowState={alertDialogState}
                    setWindowState={setAlertDialogState}
                    function={alertYesCallback}
                    okFunction={alertOkCallback}
                />
            )}
            {isLoading && (
                <LoadingScreen />
            )}

        </div>
    )
}

export default SetupRecipients